/**
 * Quick Actions Component
 *
 * Horizontal scrollable chips for rapid POI reporting.
 */

import { clsx } from 'clsx'
import { type QuickActionKey } from '@/lib/types'
import { displayAction, quickActionsFor } from '@/lib/features'

interface QuickActionsProps {
  onAction: (action: QuickActionKey) => void
  disabled?: boolean
  className?: string
}

export function QuickActions({
  onAction,
  disabled = false,
  className,
}: QuickActionsProps) {
  const actions = quickActionsFor()

  return (
    <div
      className={clsx(
        'flex overflow-x-auto gap-2 pb-2 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent',
        className
      )}
    >
      {actions.map((action) => (
        <button
          key={action.key}
          onClick={() => onAction(action.key)}
          disabled={disabled}
          className={clsx(
            'flex flex-col items-center justify-center min-w-[70px] p-2 rounded border active:scale-95 transition-all group relative overflow-hidden backdrop-blur-sm shrink-0',
            'bg-opacity-20 hover:bg-opacity-30',
            disabled && 'opacity-30 cursor-not-allowed grayscale'
          )}
          style={{
            backgroundColor: disabled ? undefined : `${action.color}15`,
            borderColor: disabled ? 'rgba(255,255,255,0.05)' : `${action.color}40`,
            boxShadow: disabled ? 'none' : `0 0 10px ${action.color}20`
          }}
        >
          <span
            className="flex flex-col items-center"
            style={{ color: disabled ? '#9ca3af' : action.color }}
          >
            <span className="material-icons-round text-lg mb-1 group-hover:animate-pulse drop-shadow-lg leading-none">
              {action.emoji}
            </span>
            <span className="text-[9px] font-bold uppercase tracking-widest font-mono leading-none whitespace-nowrap">
              {action.label}
            </span>
          </span>
        </button>
      ))}
    </div>
  )
}

/**
 * Individual Quick Action Button (for use elsewhere)
 */
interface QuickActionButtonProps {
  actionKey: QuickActionKey
  onClick: () => void
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
  className?: string
}

export function QuickActionButton({
  actionKey,
  onClick,
  disabled = false,
  size = 'md',
  showLabel = true,
  className,
}: QuickActionButtonProps) {
  const action = displayAction(actionKey)

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-[10px]',
    md: 'px-2.5 py-1 md:px-3 md:py-1.5 text-xs',
    lg: 'px-4 py-2 text-sm',
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'rounded font-bold uppercase tracking-wider transition-colors border',
        'bg-white/5 hover:bg-white/10 active:bg-white/20',
        'text-white/80 hover:text-white',
        'disabled:opacity-30 disabled:cursor-not-allowed',
        'focus:outline-none focus:ring-1 focus:ring-primary/50',
        sizeClasses[size],
        className
      )}
      style={{
        borderColor: disabled ? 'rgba(255,255,255,0.1)' : `${action.color}40`,
        color: disabled ? undefined : action.color,
      }}
    >
      <span className={showLabel ? 'mr-1' : ''}>{action.emoji}</span>
      {showLabel && action.label}
    </button>
  )
}

/**
 * POI Type Badge (for messages/markers)
 */
interface POIBadgeProps {
  type: QuickActionKey
  size?: 'sm' | 'md'
  className?: string
}

export function POIBadge({ type, size = 'md', className }: POIBadgeProps) {
  const action = displayAction(type)

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded border font-bold uppercase tracking-wider font-mono',
        size === 'sm' ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]',
        className
      )}
      style={{
        backgroundColor: `${action.color}10`,
        color: action.color,
        borderColor: `${action.color}30`
      }}
    >
      <span className={size === 'md' ? 'mr-1' : 'mr-0.5'}>{action.emoji}</span>
      {action.label}
    </span>
  )
}
